import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";

const ReceivableReportPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const params = {
        date_from: searchParams.get("date_from") || undefined,
        date_to: searchParams.get("date_to") || undefined,
        customer_id: searchParams.get("customer_id") || undefined,
        search: searchParams.get("search") || undefined,
    };

    const { data, isLoading, error } = useFetchData("report/receivable", params);

    const orders = data?.orders || [];
    const summary = data?.summary || {};

    const periodText =
        params.date_from && params.date_to
            ? `${formatDate(params.date_from)} s/d ${formatDate(params.date_to)}`
            : null;

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/receivable-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/receivable-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title = `Laporan_Piutang`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="LAPORAN PIUTANG" />

                {periodText && (
                    <div className="mb-2 text-xs">
                        <span className="font-medium">Periode: </span>
                        {periodText}
                    </div>
                )}

                <table className="mb-6 w-full border-collapse border border-black text-xs">
                    <thead>
                        <tr className="bg-default-100 whitespace-nowrap">
                            <th className="border border-black px-1 py-px text-center">No.</th>
                            <th className="border border-black px-1 py-px text-left">No. Order</th>
                            <th className="border border-black px-1 py-px text-left">Customer</th>
                            <th className="border border-black px-1 py-px text-center">Tgl. Order</th>
                            <th className="border border-black px-1 py-px text-center">Jatuh Tempo</th>
                            <th className="border border-black px-1 py-px text-center">Status Pembayaran</th>
                            <th className="border border-black px-1 py-px text-right">Total</th>
                            <th className="border border-black px-1 py-px text-right">Terbayar</th>
                            <th className="border border-black px-1 py-px text-right">Sisa</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="border border-black p-4 text-center text-default-400"
                                >
                                    Tidak ada data piutang
                                </td>
                            </tr>
                        ) : (
                            orders.map((row, index) => (
                                <tr key={row.id}>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {index + 1}.
                                    </td>
                                    <td className="border border-black px-1 py-px font-medium whitespace-nowrap">
                                        {row.order_number}
                                    </td>
                                    <td className="border border-black px-1 py-px">
                                        {row.customer || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {formatDate(row.order_date, "dd MMM yyyy")}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {row.due_date
                                            ? formatDate(row.due_date, "dd MMM yyyy")
                                            : "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {row.payment_status?.name || "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {formatCurrency(row.total_amount)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right whitespace-nowrap">
                                        {row.total_paid > 0 || row.total_discount > 0
                                            ? formatCurrency(row.total_paid + row.total_discount)
                                            : "-"}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right font-medium whitespace-nowrap">
                                        {formatCurrency(Math.abs(row.remaining))}
                                        {row.remaining < 0 ? " (lebih)" : ""}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="bg-default-100 font-bold">
                            <td
                                colSpan={6}
                                className="border border-black px-1 py-px text-right"
                            >
                                TOTAL
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency(summary.total_amount)}
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency((summary.total_paid || 0) + (summary.total_discount || 0))}
                            </td>
                            <td className="border border-black px-1 py-px text-right">
                                {formatCurrency(summary.total_remaining)}
                            </td>
                        </tr>
                    </tfoot>
                </table>

                <div className="mt-8 text-xs text-default-500">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReceivableReportPrint;
